import {
  CompleteTabapayAuthTransferInput,
  CompleteTabapayAuthTransferResultTypenames,
  CreateTabapayAuthTransferInput,
  CreateTabapayAuthTransferResultTypenames,
  MoneyInput,
  ProcessTypeInput,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import useCompleteTabapayTrasferAuth from 'features/moveMoney/screens/dcf/hooks/useCompleteTabapayTransferAuth';
import { useThreeDsHandler } from 'features/moveMoney/screens/dcf/hooks/useThreeDsHandler';
import useThreeDsSdkWeb from 'features/moveMoney/screens/dcf/hooks/useThreeDsSdkWeb';
import useCreateTabapayAuth from 'features/myVaro/screens/ExternalAccounts/hooks/useCreateTabapayAuth';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS, isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { applyKeyToAmount } from 'shared/utils/numberUtils';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

import { TabaPayEntryFlowType } from './helpers/TabaPay.helpers';

const TabapayFundingScreen = () => {
  const router = useRouter();
  const [transferAmount, setTransferAmount] = useState<string>('0.00');
  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'dcf',
  });
  const flow = useVaroOnboardingFlow();

  const isSubmitButtonDisabled = useMemo(() => {
    const amount = parseFloat(transferAmount.trim());
    return isNaN(amount) || amount <= 0;
  }, [transferAmount]);

  const { cardId } = useGlobalSearchParams();

  const handleNumPadAction = (value: string) => {
    const nextAmount = applyKeyToAmount(transferAmount, value);
    if (nextAmount !== transferAmount) {
      setTransferAmount(nextAmount ?? '');
    }
  };
  const { handleThreeDS, errorScreenThreeDs: errorThreeDsHandler } =
    useThreeDsHandler();
  const { startThreeDsWebFlow, errorScreenThreeDs: errorThreeDsWeb } =
    useThreeDsSdkWeb();

  const { createTabapayTransactionAuth, isLoading, errorScreen } =
    useCreateTabapayAuth();
  const {
    completeTabapayTransactionAuth,
    isCompleteAuthLoading,
    completeAuthErrorScreen,
  } = useCompleteTabapayTrasferAuth();

  const combinedErrorScreen =
    completeAuthErrorScreen ||
    errorScreen ||
    errorThreeDsHandler ||
    errorThreeDsWeb;

  const navigateToTabapayForm = () => {
    router.replace(
      `/${flow}/tabapay/dcf-tabapay-form?tabaPayEntryFlowType=${TabaPayEntryFlowType.DCFFlow}` as Href,
    );
  };

  const handleErrorButtonPress = () => {
    // Both "Retry" and "Add a different card" navigate to the card entry form
    navigateToTabapayForm();
  };

  const handleCompleteAuth = async (transactionId: string) => {
    const completeTransactionInput: CompleteTabapayAuthTransferInput = {
      transactionId: transactionId,
    };
    const completeAuthResult = await completeTabapayTransactionAuth(
      completeTransactionInput,
    );
    if (
      completeAuthResult?.__typename ===
      CompleteTabapayAuthTransferResultTypenames.CompleteTabapayAuthTransferSuccess
    ) {
      router.dismissTo({
        pathname:
          '/(authenticated)/move-money/tabapay/tabapay-transfer-success',
        params: { amount: transferAmount },
      });
    }
  };

  const processThreeDs = async (
    transactionId: string,
  ): Promise<boolean | undefined> => {
    if (isWeb) {
      return startThreeDsWebFlow(
        ProcessTypeInput.Transaction,
        TabaPayEntryFlowType.DCFFlow,
        undefined,
        transactionId,
      );
    }
    return handleThreeDS(
      ProcessTypeInput.Transaction,
      TabaPayEntryFlowType.DCFFlow,
      undefined,
      transactionId,
    );
  };

  const handleSubmit = async () => {
    const transactionAmount: MoneyInput = {
      amount: transferAmount,
      currency: 'USD',
    };
    const createTransactionInput: CreateTabapayAuthTransferInput = {
      externalSourceCardId: cardId as string,
      amount: transactionAmount,
    };
    try {
      const createAuthResult = await createTabapayTransactionAuth(
        createTransactionInput,
      );
      if (
        createAuthResult?.__typename ===
        CreateTabapayAuthTransferResultTypenames.CreateTabapayAuthTransferSuccess
      ) {
        setFullScreenLoading(true);
        const transactionId = createAuthResult.transactionId;
        if (createAuthResult.isChallengeRequired) {
          const threeDsResult = await processThreeDs(transactionId);
          if (threeDsResult) {
            await handleCompleteAuth(transactionId);
          }
        } else {
          await handleCompleteAuth(transactionId);
        }
      }
    } catch (error) {
      if (
        error instanceof AbortError ||
        (error instanceof DOMException && error.name === 'AbortError')
      ) {
        return;
      }
      logErrorWithContext(error, {
        featureArea: FeatureArea.PAYMENTS,
        responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
        severity: ErrorSeverity.HIGH,
        additionalContext: {
          screen: 'TabapayFundingScreen',
          method: 'handleSubmit',
        },
      });
    } finally {
      setFullScreenLoading(false);
    }
  };

  return (
    <>
      <ScreenTransitionOverlay
        isLoading={fullScreenLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          combinedErrorScreen ? (
            <View
              testID="tabapay-funding-error-container"
              style={isWeb ? styles.errorHeaderFixed : styles.errorHeader}
            >
              <View style={styles.closeIconWrapper}>
                <IconButton
                  iconKey="CLOSE"
                  iconSize={24}
                  iconOnPress={() => navigateBack()}
                  iconButtonAccessibilityLabel="close"
                />
              </View>
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={combinedErrorScreen.messageHeadline}
                messageBody={combinedErrorScreen.messageBody}
                primaryButton={{
                  title:
                    combinedErrorScreen.primaryButton?.buttonText ||
                    translatedText('closeButton'),
                  onPress: () => {
                    if (combinedErrorScreen.primaryButton?.buttonText) {
                      handleErrorButtonPress();
                    } else {
                      router.replace('/myvaro/external-accounts');
                    }
                  },
                }}
                secondaryButton={
                  combinedErrorScreen.secondaryButton
                    ? {
                        title: combinedErrorScreen.secondaryButton.buttonText,
                        onPress: () => handleErrorButtonPress(),
                      }
                    : undefined
                }
              />
            </View>
          ) : null
        }
      />
      <Stack.Screen
        options={{
          headerShown: !(isWeb && !!combinedErrorScreen),
          headerBackVisible: !(isWeb && !!combinedErrorScreen),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <View style={[styles.container, { paddingBottom: bottom }]}>
        <ScrollView
          keyboardShouldPersistTaps="never"
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.titleContainer}>
            <TitleLgText numberOfLines={0}>
              {translatedText('tabapay.title')}
            </TitleLgText>
          </View>
          <View style={styles.transferAmountContainer}>
            <ExpressiveXXLText
              numberOfLines={0}
              style={styles.updatedMYPPercentage}
            >
              {`${translatedText('currency')}${transferAmount}`}
            </ExpressiveXXLText>
            <BodyXSText style={styles.transferLimitTextStyle} numberOfLines={0}>
              {translatedText('tabapay.caption')}
            </BodyXSText>
          </View>
        </ScrollView>

        <View style={styles.numPadContainer}>
          <NumberPad
            handleButtonPress={handleNumPadAction}
            handleSubmit={handleSubmit}
            submitButtonLoading={isLoading || isCompleteAuthLoading}
            submitButtonLabel="Add money"
            submitDisabled={isSubmitButtonDisabled}
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  closeIconWrapper: {
    left: theme.spacing.xs,
    position: 'absolute',
    top: 40,
    zIndex: 10,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  errorHeaderFixed: {
    backgroundColor: theme.color.background.primary,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 9999,
  },
  numPadContainer: {
    bottom: isIOS ? theme.spacing.m : theme.spacing.xs,
    height: 328,
    marginTop: theme.spacing.s,
    padding: theme.spacing.m,
    width: '100%',
  },
  scrollContainer: {
    flexGrow: 1,
  },
  titleContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  transferAmountContainer: {
    marginTop: 50,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  transferLimitTextStyle: {
    color: theme.color.text.primary,
    textAlign: 'center',
  },
  updatedMYPPercentage: {
    textAlign: 'center',
  },
});

export default TabapayFundingScreen;
